import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { Heading, Text } from 'tio-ui/components/typography';
import { PhCaretRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// NavCard – unified navigation card with multiple layout variants
// ---------------------------------------------------------------------------

/**
 * Layout variant for the navigation card
 * - `vertical` — Icon centered on top, title below (quick action grid style)
 * - `horizontal` — Icon left, title/subtitle center, caret right (action row style)
 * - `gradient` — Vertical with gradient background (tool card style)
 */
export type NavCardLayout = 'vertical' | 'horizontal' | 'gradient';

/**
 * Color theme options for the navigation card
 */
export type NavCardColor =
  | 'violet'
  | 'indigo'
  | 'teal'
  | 'emerald'
  | 'blue'
  | 'amber'
  | 'pink'
  | 'green'
  | 'rose';

interface NavCardSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Optional subtitle text */
    subtitle?: string;
    /** Layout variant (default: 'vertical') */
    layout?: NavCardLayout;
    /** Color theme for the icon and hover effects */
    color?: NavCardColor;
  };
  Element: HTMLAnchorElement;
}

const colorClasses: Record<
  NavCardColor,
  {
    bg: string;
    text: string;
    hoverBg: string;
    hoverBorder: string;
    hoverCaret: string;
    gradient: string;
    gradientBorder: string;
  }
> = {
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/50',
    text: 'text-violet-600 dark:text-violet-400',
    hoverBg: 'group-hover:bg-violet-600 group-hover:text-white',
    hoverBorder: 'hover:ring-violet-300 dark:hover:ring-violet-600',
    hoverCaret: 'group-hover:text-violet-600 dark:group-hover:text-violet-400',
    gradient: 'from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30',
    gradientBorder: 'border-violet-100 dark:border-violet-800',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/50',
    text: 'text-indigo-600 dark:text-indigo-400',
    hoverBg: 'group-hover:bg-indigo-600 group-hover:text-white',
    hoverBorder: 'hover:ring-indigo-300 dark:hover:ring-indigo-600',
    hoverCaret: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30',
    gradientBorder: 'border-indigo-100 dark:border-indigo-800',
  },
  teal: {
    bg: 'bg-teal-100 dark:bg-teal-900/50',
    text: 'text-teal-600 dark:text-teal-400',
    hoverBg: 'group-hover:bg-teal-600 group-hover:text-white',
    hoverBorder: 'hover:ring-teal-300 dark:hover:ring-teal-600',
    hoverCaret: 'group-hover:text-teal-600 dark:group-hover:text-teal-400',
    gradient: 'from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30',
    gradientBorder: 'border-teal-100 dark:border-teal-800',
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/50',
    text: 'text-emerald-600 dark:text-emerald-400',
    hoverBg: 'group-hover:bg-emerald-600 group-hover:text-white',
    hoverBorder: 'hover:ring-emerald-300 dark:hover:ring-emerald-600',
    hoverCaret: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
    gradient: 'from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30',
    gradientBorder: 'border-emerald-100 dark:border-emerald-800',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/50',
    text: 'text-blue-600 dark:text-blue-400',
    hoverBg: 'group-hover:bg-blue-600 group-hover:text-white',
    hoverBorder: 'hover:ring-blue-300 dark:hover:ring-blue-600',
    hoverCaret: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30',
    gradientBorder: 'border-blue-100 dark:border-blue-800',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/50',
    text: 'text-amber-600 dark:text-amber-400',
    hoverBg: 'group-hover:bg-amber-600 group-hover:text-white',
    hoverBorder: 'hover:ring-amber-300 dark:hover:ring-amber-600',
    hoverCaret: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30',
    gradientBorder: 'border-amber-100 dark:border-amber-800',
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/50',
    text: 'text-pink-600 dark:text-pink-400',
    hoverBg: 'group-hover:bg-pink-600 group-hover:text-white',
    hoverBorder: 'hover:ring-pink-300 dark:hover:ring-pink-600',
    hoverCaret: 'group-hover:text-pink-600 dark:group-hover:text-pink-400',
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-900/30 dark:to-rose-900/30',
    gradientBorder: 'border-pink-100 dark:border-pink-800',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/50',
    text: 'text-green-600 dark:text-green-400',
    hoverBg: 'group-hover:bg-green-600 group-hover:text-white',
    hoverBorder: 'hover:ring-green-300 dark:hover:ring-green-600',
    hoverCaret: 'group-hover:text-green-600 dark:group-hover:text-green-400',
    gradient: 'from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30',
    gradientBorder: 'border-green-100 dark:border-green-800',
  },
  rose: {
    bg: 'bg-rose-100 dark:bg-rose-900/50',
    text: 'text-rose-600 dark:text-rose-400',
    hoverBg: 'group-hover:bg-rose-600 group-hover:text-white',
    hoverBorder: 'hover:ring-rose-300 dark:hover:ring-rose-600',
    hoverCaret: 'group-hover:text-rose-600 dark:group-hover:text-rose-400',
    gradient: 'from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30',
    gradientBorder: 'border-rose-100 dark:border-rose-800',
  },
};

/**
 * NavCard - A unified navigation card with layout variants
 *
 * Consolidates QuickActionLink (vertical), ActionLinkCard (horizontal),
 * and ToolCard (gradient) into a single component with a `@layout` arg.
 *
 * @example Vertical (quick action grid)
 * ```gts
 * import NavCard from 'tio-reimagined/components/nav-card';
 *
 * <NavCard @route="authenticated.explore" @icon={{PhCompass}} @title="Explore" @subtitle="150+ paths" @color="violet" />
 * ```
 *
 * @example Horizontal (action row)
 * ```gts
 * <NavCard @layout="horizontal" @route="authenticated.tas" @icon={{PhPlus}} @title="Start Application" @subtitle="Request funding" @color="indigo" />
 * ```
 *
 * @example Gradient (tool card)
 * ```gts
 * <NavCard @layout="gradient" @route="authenticated.coaching" @icon={{PhChatCircleDots}} @title="Loan Coaches" @subtitle="Expert guidance" @color="teal" />
 * ```
 */
export default class NavCard extends Component<NavCardSignature> {
  get layout() {
    return this.args.layout ?? 'vertical';
  }

  get color() {
    return this.args.color ?? 'violet';
  }

  get colors() {
    return colorClasses[this.color];
  }

  get isVertical() {
    return this.layout === 'vertical';
  }

  get isHorizontal() {
    return this.layout === 'horizontal';
  }

  get isGradient() {
    return this.layout === 'gradient';
  }

  <template>
    {{#if this.isHorizontal}}
      {{! Horizontal layout – icon left, text center, caret right }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="horizontal"
        @route={{@route}}
        class="flex items-center gap-4 p-5 bg-white dark:bg-gray-900 rounded-xl shadow-sm ring-1 ring-gray-950/[0.04] dark:ring-gray-700 hover:shadow-md
          {{this.colors.hoverBorder}}
          transition-all group"
        ...attributes
      >
        <div
          data-test-nav-card-icon
          class="p-3 {{this.colors.bg}} rounded-xl {{this.colors.text}} transition-colors"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <div class="flex-1">
          <div data-test-nav-card-title class="font-semibold text-gray-900 dark:text-white">
            {{@title}}
          </div>
          {{#if @subtitle}}
            <Text data-test-nav-card-subtitle @intent="muted" class="text-sm">{{@subtitle}}</Text>
          {{/if}}
        </div>
        <PhCaretRight
          data-test-nav-card-caret
          @size={{20}}
          class="text-gray-400 dark:text-gray-500 {{this.colors.hoverCaret}}"
        />
      </LinkTo>
    {{else if this.isGradient}}
      {{! Gradient layout – vertical with gradient background }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="gradient"
        @route={{@route}}
        class="flex flex-col items-center gap-2 p-4 bg-gradient-to-br
          {{this.colors.gradient}}
          rounded-xl hover:shadow-md transition-all border
          {{this.colors.gradientBorder}}
          text-center"
        ...attributes
      >
        <div data-test-nav-card-icon class="p-3 {{this.colors.bg}} rounded-xl">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} class={{this.colors.text}} />
          {{/let}}
        </div>
        <span data-test-nav-card-title class="text-sm font-medium text-gray-900 dark:text-white">
          {{@title}}
        </span>
        {{#if @subtitle}}
          <span
            data-test-nav-card-subtitle
            class="text-xs text-gray-500 dark:text-gray-400"
          >{{@subtitle}}</span>
        {{/if}}
      </LinkTo>
    {{else}}
      {{! Vertical layout (default) – icon on top, text below }}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="vertical"
        @route={{@route}}
        class="bg-white dark:bg-gray-900 rounded-lg p-4 shadow-sm ring-1 ring-gray-950/[0.04] dark:ring-gray-700 hover:shadow-md
          {{this.colors.hoverBorder}}
          transition-all group text-center"
        ...attributes
      >
        <div
          data-test-nav-card-icon
          class="p-3
            {{this.colors.bg}}
            {{this.colors.text}}
            rounded-lg
            {{this.colors.hoverBg}}
            transition-colors mx-auto w-fit mb-2"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <Heading
          data-test-nav-card-title
          @level={{3}}
          class="text-sm font-semibold text-gray-900 dark:text-white"
        >{{@title}}</Heading>
        {{#if @subtitle}}
          <Text
            data-test-nav-card-subtitle
            @intent="muted"
            class="text-xs mt-1 hidden sm:block dark:text-gray-400"
          >{{@subtitle}}</Text>
        {{/if}}
      </LinkTo>
    {{/if}}
  </template>
}

export type { NavCardSignature };
